import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import CommentIcon from '@mui/icons-material/Comment';
import FeedbackIcon from '@mui/icons-material/Feedback';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { styled } from '@mui/material/styles';
import * as Features from '../../../../../../../shared/Features';
import { Translate } from '../../../../../../../shared/LanguageService';
import { useApiUrl } from '../../../../../../../shared/hooks/useApiUrl';
import SwipeListener from '../../../../../../../shared/SwipeListener';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { ImageLoadingSkeleton } from '../../../shared/imageCard/view/ImageLoadingSkeleton';
import { useImagePreviewNavigation } from '../../../shared/imageNavigation/state/use-image-preview-navigation';
import { ImagePreviewNavigation } from '../../../shared/imageNavigation/view/ImagePreviewNavigation';
import { ImageEditorPhotoStatus } from '../../entry/queries/use-query-photo-status';
import { canUploadEditedImages } from '../../uploadEditedImages/state/utils';
import { EditedImageDisplay } from './EditedImageDisplay';
import { EditedImageDropzone } from './EditedImageDropzone';
import { ImageCornerButton } from './ImageCornerButton';
import { useDownloadImageMutation } from '../queries/use-mutation-download-image';
import { usePrefetchAdjacentEditedImages } from '../queries/use-prefetch-adjacent-edited-images';
import { ImageVersion, useEditedImageQuery } from '../queries/use-query-edited-image';
import {
    formatImageDetails,
    formatImagePosition,
    getDefaultVersionId,
    getImageVersionTabs,
    getVersionStatuses,
    VersionStatusIcon
} from '../state/image-versions';
import { canViewComments } from '../../imageComments/state/utils';
import { ImageCommentsSection } from '../../imageComments/view/ImageCommentsSection';
import { ImageCommentsSkeleton } from '../../imageComments/view/ImageCommentsSkeleton';
import { FullScreenImageView } from './FullScreenImageView';

const StyledDialog = styled(Dialog)({
    '& .MuiDialog-paper': {
        width: 'calc(100vw - 64px)',
        height: 'calc(100vh - 64px)',
        maxWidth: 'none',
        maxHeight: 'none',
        margin: '32px',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: '#dde3e3',
        display: 'flex',
        flexDirection: 'column'
    }
});

const Header = styled('header')({
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '8px 16px',
    backgroundColor: '#dde3e3',
    color: '#333',
    minHeight: '48px'
});

const HeaderHeading = styled('h2')({
    margin: 0,
    fontSize: '14px',
    fontWeight: 400,
    color: '#333',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
});

const Spacer = styled('div')({
    display: 'flex',
    flex: '1 auto'
});

const Columns = styled('div')({
    display: 'flex',
    flex: 1,
    gap: '16px',
    padding: '0 16px 16px',
    overflow: 'hidden'
});

const ImageColumn = styled('section')({
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 60%',
    maxWidth: '60%',
    minWidth: 0,
    gap: '8px'
});

const Footer = styled('footer')({
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '0 16px 16px'
});

const DetailsColumn = styled('aside')({
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '8px'
});

const VersionTabs = styled(Tabs)({
    minHeight: '40px',
    '& .MuiTab-root': {
        minHeight: '40px',
        textTransform: 'none',
        fontSize: '14px'
    }
});

const ImageDetails = styled(Typography)({
    fontSize: '13px',
    color: '#333'
}) as typeof Typography;

const VersionTabsSkeleton = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    minHeight: '40px',
    padding: '0 16px'
});

const TabLabel = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
});

const STATUS_ICONS = {
    [VersionStatusIcon.Comments]: CommentIcon,
    [VersionStatusIcon.NeedsEditing]: FeedbackIcon
};

type VersionTabLabelProps = {
    label: string;
    version: ImageVersion;
    isLatest: boolean;
};

function VersionTabLabel({ label, version, isLatest }: VersionTabLabelProps) {
    let statuses = getVersionStatuses(version, isLatest);

    return (
        <TabLabel>
            {label}
            {statuses.map((status) => {
                let Icon = STATUS_ICONS[status.icon];
                return (
                    <Icon
                        key={status.icon}
                        role="img"
                        aria-label={status.title}
                        titleAccess={status.title}
                        sx={{ fontSize: 18 }}
                        data-testid={`version-status-${status.icon}`}
                    />
                );
            })}
        </TabLabel>
    );
}

type EditedImagePreviewContentProps = {
    orderId: number;
    imageId: number;
    imageIds: number[];
    user: AuthenticatedUser;
    versions: ImageVersion[];
    isLoading: boolean;
    canViewFullScreen: boolean;
    canDownload: boolean;
    canUpload: boolean;
    onImageReplaced: (newImageId: number) => void;
    onSwipeLeft: () => void;
    onSwipeRight: () => void;
};

function EditedImagePreviewContent({
    orderId,
    imageId,
    imageIds,
    user,
    versions,
    isLoading,
    canViewFullScreen,
    canDownload,
    canUpload,
    onImageReplaced,
    onSwipeLeft,
    onSwipeRight
}: EditedImagePreviewContentProps) {
    let apiUrl = useApiUrl();
    let downloadImage = useDownloadImageMutation({ orderId, user });
    let [selectedVersionId, setSelectedVersionId] = React.useState<number | null>(null);
    let [finishedVersionId, setFinishedVersionId] = React.useState<number | null>(null);
    let [isFullScreen, setIsFullScreen] = React.useState(false);
    let tabs = getImageVersionTabs(versions);
    let currentVersionId =
        selectedVersionId !== null ? selectedVersionId : getDefaultVersionId(versions);
    let currentTab = tabs.find((tab) => tab.id === currentVersionId);
    let currentVersion = currentTab ? currentTab.version : null;
    let latestVersionId = getDefaultVersionId(versions);
    let hasImageFinishedLoading =
        currentVersion !== null && finishedVersionId === currentVersion.id;
    let isWaitingForImage = isLoading || (currentVersion !== null && !hasImageFinishedLoading);
    let canDownloadCurrentVersion =
        currentVersion !== null && Boolean(currentVersion.highResImageReference);
    let fullScreenReference = currentVersion
        ? currentVersion.highResImageReference || currentVersion.reference
        : '';

    usePrefetchAdjacentEditedImages({
        orderId,
        imageId,
        imageIds,
        enabled: hasImageFinishedLoading
    });

    return (
        <>
            <DialogContent
                sx={{
                    display: 'flex',
                    flex: 1,
                    padding: 0,
                    overflow: 'hidden',
                    '&.MuiDialogContent-root': { padding: 0 }
                }}
            >
                <Columns>
                    <ImageColumn aria-busy={isWaitingForImage}>
                        {isLoading && (
                            <VersionTabsSkeleton
                                data-testid="version-tabs-skeleton"
                                aria-hidden="true"
                            >
                                <Skeleton variant="text" animation="wave" width={64} />
                                <Skeleton variant="text" animation="wave" width={120} />
                            </VersionTabsSkeleton>
                        )}
                        {tabs.length > 0 && (
                            <VersionTabs
                                value={currentTab ? currentTab.id : false}
                                onChange={(event, versionId: number) =>
                                    setSelectedVersionId(versionId)
                                }
                                variant="scrollable"
                                scrollButtons="auto"
                                aria-label={Translate(
                                    'image-processing/modal/title/image-showcase'
                                )}
                            >
                                {tabs.map((tab) => (
                                    <Tab
                                        key={tab.id}
                                        value={tab.id}
                                        label={
                                            <VersionTabLabel
                                                label={tab.label}
                                                version={tab.version}
                                                isLatest={tab.id === latestVersionId}
                                            />
                                        }
                                        data-testid={`image-version-${tab.kind}`}
                                    />
                                ))}
                            </VersionTabs>
                        )}
                        <SwipeListener
                            onSwipeLeft={onSwipeLeft}
                            onSwipeRight={onSwipeRight}
                            style={{ display: 'flex', flex: 1, minHeight: 0 }}
                        >
                            <EditedImageDropzone
                                orderId={orderId}
                                imageId={imageId}
                                user={user}
                                canUpload={canUpload}
                                onReplaced={onImageReplaced}
                            >
                                {(pickFileButton) =>
                                    currentVersion ? (
                                        <EditedImageDisplay
                                            key={currentVersion.id}
                                            src={`${apiUrl}/file/${currentVersion.reference}`}
                                            alt={currentVersion.filename}
                                            onLoad={() => setFinishedVersionId(currentVersion.id)}
                                            onError={() => setFinishedVersionId(currentVersion.id)}
                                            overlay={
                                                <>
                                                    {canViewFullScreen && (
                                                        <ImageCornerButton
                                                            corner="left"
                                                            aria-label={Translate(
                                                                'image-processing/modal/button-label/full-screen'
                                                            )}
                                                            data-testid="open-full-screen"
                                                            onClick={() => setIsFullScreen(true)}
                                                        >
                                                            <FullscreenIcon />
                                                        </ImageCornerButton>
                                                    )}
                                                    {pickFileButton}
                                                </>
                                            }
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                flex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <ImageLoadingSkeleton />
                                        </Box>
                                    )
                                }
                            </EditedImageDropzone>
                        </SwipeListener>
                        {hasImageFinishedLoading && (
                            <ImageDetails component="p" data-testid="image-details">
                                {formatImageDetails(currentVersion)}
                            </ImageDetails>
                        )}
                        {isWaitingForImage && (
                            <Skeleton
                                variant="text"
                                animation="wave"
                                width={220}
                                sx={{ fontSize: '13px' }}
                                data-testid="image-details-skeleton"
                            />
                        )}
                    </ImageColumn>
                    <DetailsColumn>
                        {canViewComments(user) && hasImageFinishedLoading && (
                            <ImageCommentsSection
                                orderId={orderId}
                                user={user}
                                version={currentVersion}
                                isLatestVersion={currentVersion.id === latestVersionId}
                            />
                        )}
                        {canViewComments(user) && isWaitingForImage && <ImageCommentsSkeleton />}
                    </DetailsColumn>
                </Columns>
            </DialogContent>
            {canDownload && (isLoading || canDownloadCurrentVersion) && (
                <Footer>
                    <Button
                        variant="contained"
                        startIcon={<FileDownloadIcon />}
                        disabled={!canDownloadCurrentVersion || downloadImage.isLoading}
                        onClick={() => downloadImage.mutate(currentVersion)}
                    >
                        {Translate('order/image-processing/button-toolbar/button/download')}
                    </Button>
                </Footer>
            )}
            {currentVersion && (
                <FullScreenImageView
                    open={isFullScreen}
                    src={`${apiUrl}/file/${fullScreenReference}`}
                    alt={currentVersion.filename}
                    onClose={() => setIsFullScreen(false)}
                />
            )}
        </>
    );
}

type EditedImagePreviewDialogProps = {
    open: boolean;
    orderId: number;
    imageId: number;
    user: AuthenticatedUser;
    filename: string;
    imageIds: number[];
    positionLabel: string;
    versions: ImageVersion[];
    isLoading: boolean;
    canViewFullScreen: boolean;
    canDownload: boolean;
    canUpload: boolean;
    onClose: () => void;
    onImageReplaced: (newImageId: number) => void;
    onNavigate: (imageId: number) => void;
};

function EditedImagePreviewDialog({
    open,
    orderId,
    imageId,
    user,
    filename,
    imageIds,
    positionLabel,
    versions,
    isLoading,
    canViewFullScreen,
    canDownload,
    canUpload,
    onClose,
    onImageReplaced,
    onNavigate
}: EditedImagePreviewDialogProps) {
    let navigation = useImagePreviewNavigation({
        idList: imageIds,
        currentImageId: imageId,
        onNavigate,
        enabled: open
    });

    return (
        <StyledDialog open={open} onClose={onClose} aria-labelledby="edited-image-preview-title">
            <Header>
                <HeaderHeading id="edited-image-preview-title">
                    {`${Translate('image-processing/modal/title/image-showcase')}: ${filename}`}
                </HeaderHeading>
                {imageIds.length > 1 && (
                    <ImagePreviewNavigation
                        onPrevious={navigation.onSwipeLeft}
                        onNext={navigation.onSwipeRight}
                        canGoPrevious={navigation.canGoLeft}
                        canGoNext={navigation.canGoRight}
                        counterLabel={positionLabel}
                    />
                )}
                <Spacer />
                <IconButton
                    aria-label={Translate('image-processing/modal/footer/button-label/close')}
                    onClick={onClose}
                    sx={{ color: '#333' }}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </Header>
            <EditedImagePreviewContent
                key={imageId}
                orderId={orderId}
                imageId={imageId}
                imageIds={imageIds}
                user={user}
                versions={versions}
                isLoading={isLoading}
                canViewFullScreen={canViewFullScreen}
                canDownload={canDownload}
                canUpload={canUpload}
                onImageReplaced={onImageReplaced}
                onSwipeLeft={navigation.onSwipeLeft}
                onSwipeRight={navigation.onSwipeRight}
            />
        </StyledDialog>
    );
}

type DataLayerProps = {
    orderId: number;
    imageId: number | null;
    imageIds: number[];
    filename: string;
    user: AuthenticatedUser;
    photoStatus: ImageEditorPhotoStatus;
    onClose: () => void;
    onImageReplaced: (newImageId: number) => void;
    onNavigate: (imageId: number) => void;
};

export default function DataLayer({
    orderId,
    imageId,
    imageIds,
    filename,
    user,
    photoStatus,
    onClose,
    onImageReplaced,
    onNavigate
}: DataLayerProps) {
    let [shownImage, setShownImage] = React.useState({ imageId, filename });
    if (imageId !== null && (imageId !== shownImage.imageId || filename !== shownImage.filename)) {
        setShownImage({ imageId, filename });
    }
    let editedImage = useEditedImageQuery({ orderId, imageId: shownImage.imageId, user });

    return (
        <EditedImagePreviewDialog
            open={imageId !== null}
            orderId={orderId}
            imageId={shownImage.imageId === null ? 0 : shownImage.imageId}
            user={user}
            filename={shownImage.filename}
            imageIds={imageIds}
            positionLabel={formatImagePosition(imageIds, shownImage.imageId)}
            versions={editedImage.data ? editedImage.data.imageShowcaseViewModels : []}
            isLoading={editedImage.isLoading}
            canViewFullScreen={Features.hasFeature(Features.ViewHighResolutionImageZoom, user)}
            canDownload={Features.hasFeature(Features.DownloadHighResolutionImage, user)}
            canUpload={canUploadEditedImages(user, photoStatus)}
            onClose={onClose}
            onImageReplaced={onImageReplaced}
            onNavigate={onNavigate}
        />
    );
}
